var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from "react";
import { Row, Col, Button, MessageBanner } from "@folio/stripes/components";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { firstByDisplayOrder } from "../../utils/selectMappers";
import { useOnixOptions } from "../../hooks/title/useOnixOptions";
import { useItemEditor } from "../../hooks/title/useItemEditor";
import { useContentRows } from "../../utils/toContentRows";
import { SubjectFormModal } from "./SubjectFormModal";
import { useFieldArrayErrors } from "../../hooks/useFieldArrayErrors";
import { hasThemaSubjectCategory } from "../../utils/themaSubjectRules";
import { KEYWORDS_SCHEME, removeSubjectWithMainRules, saveSubjectWithMainRules, schemesMissingMainSubject, } from "../../utils/mainSubjectRules";
import { EntityRowList } from "./EntityRowList";
import css from "./FormModal.module.css";
var TitleSubjectsForm = function () {
    var _a, _b;
    var formatMessage = useIntl().formatMessage;
    var _c = useFormContext(), control = _c.control, getValues = _c.getValues;
    var errorMessages = useFieldArrayErrors("subjects");
    var _d = useFieldArray({
        control: control,
        name: "subjects",
    }), subjectFields = _d.fields, replaceSubjects = _d.replace;
    // Main subject per scheme (issue #76113): every change of the list goes
    // through the shared rules, which may also change other rows of the same
    // scheme — so the whole array is replaced (the table renders the fields).
    var currentSubjects = function () { var _a; return (_a = getValues("subjects")) !== null && _a !== void 0 ? _a : []; };
    var appendSubject = function (item) {
        return replaceSubjects(saveSubjectWithMainRules(currentSubjects(), item, null));
    };
    var updateSubject = function (index, item) {
        return replaceSubjects(saveSubjectWithMainRules(currentSubjects(), item, index));
    };
    var watchedSubjects = useWatch({ control: control, name: "subjects" });
    var _e = useOnixOptions(), subjectSchemeTable = _e.subjectSchemeTable, subjectSchemeLoading = _e.subjectSchemeLoading, subjectSchemeStatus = _e.subjectSchemeStatus;
    var _f = useItemEditor({
        appendItem: appendSubject,
        updateItem: updateSubject,
        createEmpty: function () {
            var firstScheme = firstByDisplayOrder(subjectSchemeTable === null || subjectSchemeTable === void 0 ? void 0 : subjectSchemeTable.onixCodetableItems);
            return {
                scheme: firstScheme,
                // SubjectCode holds the term code, not the scheme code — start empty.
                code: "",
                text: "",
                mainSubject: false,
            };
        },
    }), isOpen = _f.isOpen, isEditing = _f.isEditing, editingIndex = _f.editingIndex, draft = _f.draft, setDraft = _f.setDraft, openEditor = _f.openEditor, closeEditor = _f.closeEditor, saveItem = _f.saveItem;
    var handleSave = function () {
        var _a;
        // Keywords (scheme 20) never carry a SubjectCode — strip any stale code
        // (e.g. legacy "20") so it is not exported.
        var isKeywords = ((_a = draft.scheme) === null || _a === void 0 ? void 0 : _a.value) === KEYWORDS_SCHEME;
        saveItem(isKeywords ? __assign(__assign({}, draft), { code: "" }) : undefined);
    };
    var handleRemove = function (index) {
        return replaceSubjects(removeSubjectWithMainRules(currentSubjects(), index));
    };
    var schemesWithoutMain = schemesMissingMainSubject(watchedSubjects);
    var schemeLabel = function (scheme) {
        var _a, _b;
        return ((_b = (_a = (watchedSubjects !== null && watchedSubjects !== void 0 ? watchedSubjects : []).find(function (subject) { var _a; return ((_a = subject.scheme) === null || _a === void 0 ? void 0 : _a.value) === scheme; })) === null || _a === void 0 ? void 0 : _a.scheme) === null || _b === void 0 ? void 0 : _b.description) || scheme;
    };
    // The subject being edited is excluded: changing the only 93 term to
    // another scheme must not count as a category for itself.
    var otherSubjectsHaveThemaCategory = hasThemaSubjectCategory(((_a = getValues("subjects")) !== null && _a !== void 0 ? _a : []).filter(function (_, i) { return i !== editingIndex; }));
    var contentData = useContentRows(subjectFields);
    var columns = [
        {
            name: formatMessage({
                id: "ui-br-agency.title.subjects.mainSubject.label",
            }),
            cellRenderer: function (row) {
                return row.mainSubject
                    ? formatMessage({ id: "ui-br-agency.yes" })
                    : formatMessage({ id: "ui-br-agency.no" });
            },
        },
        {
            name: formatMessage({ id: "ui-br-agency.title.subjects.scheme.label" }),
            cellRenderer: function (row) { var _a; return ((_a = row.scheme) === null || _a === void 0 ? void 0 : _a.description) || "-"; },
        },
        {
            name: formatMessage({ id: "ui-br-agency.title.subjects.code.label" }),
            cellRenderer: function (row) { return row.code || "-"; },
        },
        {
            name: formatMessage({ id: "ui-br-agency.title.subjects.text.label" }),
            cellRenderer: function (row) { return row.text || "-"; },
        },
    ];
    return (React.createElement(React.Fragment, null,
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12 }, subjectFields.length > 0 ? (React.createElement(EntityRowList, { data: contentData, columns: columns, onEdit: function (row) {
                    return openEditor(getValues("subjects.".concat(row._index)), row._index);
                }, onDelete: function (row) { return handleRemove(row._index); } })) : (React.createElement("div", { className: css.emptyState },
                React.createElement(FormattedMessage, { id: "ui-br-agency.title.subjects.empty" }))))),
        schemesWithoutMain.length > 0 && (React.createElement(Row, null,
            React.createElement(Col, { xs: 12 },
                React.createElement(MessageBanner, { type: "warning", className: css.tableErrorBanner },
                    React.createElement("div", null,
                        React.createElement(FormattedMessage, { id: "ui-br-agency.title.subjects.warning.missingMain" })),
                    React.createElement("div", null,
                        React.createElement(FormattedMessage, { id: "ui-br-agency.title.subjects.warning.missingMainSchemes", values: {
                                schemes: schemesWithoutMain.map(schemeLabel).join(", "),
                            } })))))),
        errorMessages.length > 0 && (React.createElement(Row, { "data-error-field": "subjects" },
            React.createElement(Col, { xs: 12 },
                React.createElement(MessageBanner, { type: "error", className: css.tableErrorBanner }, errorMessages.map(function (msg) { return (React.createElement("div", { key: msg }, msg)); }))))),
        React.createElement(SubjectFormModal, { open: isOpen, isEditing: isEditing, draft: draft, onDraftChange: setDraft, onSave: handleSave, onClose: closeEditor, subjectSchemeItems: (_b = subjectSchemeTable === null || subjectSchemeTable === void 0 ? void 0 : subjectSchemeTable.onixCodetableItems) !== null && _b !== void 0 ? _b : [], subjectSchemeLoading: subjectSchemeLoading, subjectSchemeStatus: subjectSchemeStatus, hasThemaSubjectCategory: otherSubjectsHaveThemaCategory }),
        React.createElement(Row, { className: css.actions },
            React.createElement(Col, { xs: 12 },
                React.createElement(Button, { buttonStyle: "primary", onClick: function () { return openEditor(); } },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.title.form.add.subject" }))))));
};
export default TitleSubjectsForm;
